/**
 * Tabla de precios de membresía (Sprint 25-2).
 *
 * `semestral` y `annual` son los valores REALES del business model
 * (`docs/business/06-business-model.md` — decisión mayo 20, 2026):
 * Esencial $6.3M/6 meses y $11.2M/año (anual -11% vs. semestral); Pro
 * $12.5M/6 meses y $22M/año (anual -12% vs. semestral).
 *
 * ─────────────────────────────────────────────────────────────────────
 * PROVISIONAL: solo `trimestral`. El período entró al portafolio con la
 * decisión del founder del 1-sep-2026 (trimestral / semestral / anual,
 * mensual sale) y todavía NO tiene precio en el business model — los
 * dos valores de abajo se inventaron para poder maquetar el flujo de
 * renovación (M2) sin bloquear el sprint en la definición comercial:
 * decisión #38 (`docs/ops/pending-decisions.md`), pendiente de
 * resolución en Sprint 26. `TRIMESTRAL_ES_MOCK` marca esto
 * explícitamente para que ningún consumidor (UI o test) lo trate como
 * definitivo sin darse cuenta — de ahí que el badge "provisional" de
 * `RenovarMembresia` salga solo con trimestral seleccionado, nunca con
 * semestral. NO commitear un cambio que borre este flag sin resolver
 * antes la decisión #38.
 * ─────────────────────────────────────────────────────────────────────
 *
 * `MembresiaTier`/`MembresiaPeriod` viven en `lib/api/membresia.ts` (son
 * el contrato de wire con el backend) y se re-importan acá en vez de
 * redeclararse — una sola fuente de verdad para el set de valores
 * validos.
 */

import type { MembresiaPeriod, MembresiaTier } from "@/lib/api";

export type { MembresiaPeriod, MembresiaTier };

/** True mientras trimestral siga siendo mock (semestral y anual son reales) — ver decisión #38. */
export const TRIMESTRAL_ES_MOCK = true;

export const MEMBRESIA_PRECIOS: Record<MembresiaTier, Record<MembresiaPeriod, number>> = {
  esencial: {
    trimestral: 3_300_000,
    semestral: 6_300_000,
    annual: 11_200_000,
  },
  pro: {
    trimestral: 6_500_000,
    semestral: 12_500_000,
    annual: 22_000_000,
  },
};

/** Formatea un monto en pesos colombianos sin decimales: `11200000` -> `"$ 11.200.000"`. */
export function formatoCOP(valor: number): string {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(valor);
}

/**
 * Cobro de pasar de Esencial a Pro con el período vigente (issue #734,
 * decisión #44): la diferencia de precio del período pagado, dividida por
 * los días de ese período y multiplicada por los días que quedan (hoy
 * incluido). Con el período entero por delante es la diferencia completa.
 * Sin período con precio (legacy `monthly`), se usa el anual.
 *
 * Lo calcula el navegador porque el pago es simulado: la pasarela real
 * (#34) tiene que calcularlo en el backend antes de cobrar.
 */
export function cobroCambioAPro({
  periodoPagado,
  diasRestantes,
  diasDelPeriodo,
}: {
  periodoPagado: MembresiaPeriod | null;
  diasRestantes: number;
  diasDelPeriodo: number;
}): number {
  const periodo = periodoPagado ?? "annual";
  const diferencia = MEMBRESIA_PRECIOS.pro[periodo] - MEMBRESIA_PRECIOS.esencial[periodo];
  return Math.round((diferencia * diasRestantes) / diasDelPeriodo);
}
